import React from 'react';
import { useWidgetStore } from '../hooks';
import { useWidgetConfig } from '../context';
import type { WidgetTab } from '../types';
import {
  GraduationCapIcon,
  BookIcon,
  MegaphoneIcon,
  ChatIcon,
  CloseIcon,
} from '../icons';

const tabMeta: Record<
  WidgetTab,
  { label: string; Icon: React.FC<{ size?: number }> }
> = {
  tutorials: { label: 'Learn', Icon: GraduationCapIcon },
  docs: { label: 'Docs', Icon: BookIcon },
  releases: { label: 'Updates', Icon: MegaphoneIcon },
  charlotte: { label: 'Chat', Icon: ChatIcon },
};

export function PanelHeader() {
  const config = useWidgetConfig();
  const activeTab = useWidgetStore((s) => s.activeTab);
  const setTab = useWidgetStore((s) => s.setTab);
  const close = useWidgetStore((s) => s.close);

  return (
    <div
      style={{
        borderBottom: '1px solid var(--sw-border, #2d2d44)',
        padding: '0',
        userSelect: 'none',
      }}
    >
      {/* Top row: product name + close button */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px 8px',
        }}
      >
        <span
          style={{
            fontWeight: 600,
            fontSize: '14px',
            color: 'var(--sw-text-primary, #e0e0e0)',
            letterSpacing: '0.02em',
          }}
        >
          {config.productName} Support
        </span>
        <button
          type="button"
          onClick={close}
          aria-label="Close support panel"
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--sw-text-secondary, #9a9ab0)',
            padding: '4px',
            minWidth: '48px',
            minHeight: '48px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 'var(--sw-border-radius, 8px)',
            transition: 'color 0.15s',
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLElement).style.color =
              'var(--sw-text-primary, #e0e0e0)';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLElement).style.color =
              'var(--sw-text-secondary, #9a9ab0)';
          }}
        >
          <CloseIcon size={18} />
        </button>
      </div>

      {/* Tab bar */}
      <div
        role="tablist"
        style={{
          display: 'flex',
          gap: '0',
          padding: '0 12px',
        }}
      >
        {config.tabs.map((tab) => {
          const meta = tabMeta[tab];
          if (!meta) return null;
          const isActive = tab === activeTab;
          return (
            <button
              key={tab}
              type="button"
              role="tab"
              aria-selected={isActive}
              aria-label={`${meta.label} tab`}
              onClick={() => setTab(tab)}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                padding: '8px 4px',
                minHeight: '48px',
                minWidth: '48px',
                border: 'none',
                borderBottom: isActive
                  ? '2px solid var(--sw-accent-primary, #4cc9f0)'
                  : '2px solid transparent',
                background: 'none',
                color: isActive
                  ? 'var(--sw-accent-primary, #4cc9f0)'
                  : 'var(--sw-text-secondary, #9a9ab0)',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: isActive ? 600 : 400,
                transition: 'color 0.15s, border-color 0.15s',
                whiteSpace: 'nowrap',
              }}
            >
              <meta.Icon size={16} />
              {meta.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
